import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Car, PoundSterling, Users, Clock, Shield, Phone, ArrowRight, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { SEO } from '../components/SEO';

export default function Franchise() {
  const soloFeatures = [
    'Use the Top Driver brand and reputation',
    'Access to our pupil enquiry system',
    'Professional marketing support',
    'Use of our branded vehicle livery (optional)',
    'Admin and booking support',
    'Part of a trusted Liverpool driving school',
    'Flexible - work when you want',
    'No long-term contracts - leave anytime',
    'Keep more of your earnings'
  ];

  const whoIsItFor = [
    {
      title: 'Experienced ADIs',
      description: 'Instructors who have already established a local reputation and client base but want the backing of a recognised brand.'
    },
    {
      title: 'Transitioning Instructors',
      description: 'Those looking to move from a full franchise with another school to a more independent setup with lower overheads.'
    },
    {
      title: 'Independent Instructors',
      description: 'Fully independent instructors who want access to more pupils and marketing support without giving up their freedom.'
    },
    {
      title: 'Part-Time Instructors',
      description: 'Qualified ADIs who teach part-time and want flexibility without the commitment of a full franchise fee.'
    }
  ];

  const benefits = [
    {
      icon: PoundSterling,
      title: 'Low Weekly Fee',
      description: 'Just £50 per week - keep more of what you earn'
    },
    {
      icon: Users,
      title: 'Pupil Leads',
      description: 'Access to our enquiry system and new pupil referrals'
    },
    {
      icon: Shield,
      title: 'Trusted Brand',
      description: 'Work under the established Top Driver name in Liverpool'
    },
    {
      icon: Clock,
      title: 'Total Flexibility',
      description: 'Set your own hours, holidays, and working area'
    }
  ];

  const faqs = [
    {
      question: 'What qualifications do I need?',
      answer: 'You can join us as a fully qualified ADI (green badge) or as a PDI (pink badge holder). Both are welcome to join the Top Driver family. If you\'re a PDI, this is a great opportunity to build your experience while benefiting from our support and pupil leads.'
    },
    {
      question: 'Do I need my own car?',
      answer: 'Yes, you will need to provide your own dual-controlled vehicle. We can advise on vehicle options and livery if needed.'
    },
    {
      question: 'How does the pupil referral system work?',
      answer: 'When we receive enquiries in your area, we will pass them directly to you. You handle the booking and keep 100% of the lesson fees - the £50 weekly fee covers everything.'
    },
    {
      question: 'Is there a minimum contract period?',
      answer: 'No long-term contracts. We ask for just 4 weeks notice if you decide to leave. We believe in earning your loyalty, not locking you in.'
    },
    {
      question: 'Can I keep my existing pupils?',
      answer: 'Absolutely! Your existing pupils are yours. The Solo franchise simply gives you access to additional support and new pupil leads.'
    },
    {
      question: 'What areas do you cover?',
      answer: 'We primarily operate across Liverpool and surrounding areas including Norris Green, Croxteth, West Derby, Fazakerley, Aintree, Kirkby, and more.'
    }
  ];

  return (
    <>
      <SEO 
        title="ADI Franchise Opportunity Liverpool | Solo Option £50/week | Top Driver"
        description="Join Top Driver as a franchise instructor. Solo option just £50 per week for qualified ADIs. Keep your independence, get pupil leads and brand support. Liverpool's trusted driving school."
        keywords="driving instructor franchise liverpool, ADI franchise, solo driving instructor, driving school franchise, instructor opportunity liverpool"
        canonical="https://topdriver.co.uk/franchise"
      />

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-red-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <Car className="w-4 h-4 mr-2" />
                For Qualified ADI Instructors
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-black mb-6">
                Solo Franchise <span className="text-red-600">£50/week</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Keep your independence while benefiting from the Top Driver brand. Access pupil leads, marketing support, and a trusted reputation - all for just £50 per week.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  asChild
                  size="lg"
                  className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg"
                >
                  <Link to="/contact">
                    Apply Now
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white px-8 py-6 text-lg"
                >
                  <a href="tel:07960087298">
                    <Phone className="mr-2 w-5 h-5" />
                    Call Us
                  </a>
                </Button>
              </div>
            </div>
            <div className="relative">
              <Card className="border-2 border-red-600 shadow-2xl">
                <CardContent className="p-8">
                  <div className="text-center mb-6">
                    <p className="text-gray-600 mb-2">Solo Franchise</p>
                    <div className="flex items-center justify-center">
                      <span className="text-5xl font-bold text-red-600">£50</span>
                      <span className="text-gray-600 ml-2">/week</span>
                    </div>
                  </div>
                  <ul className="space-y-3">
                    {soloFeatures.slice(0, 6).map((feature) => (
                      <li key={feature} className="flex items-start space-x-3">
                        <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    asChild
                    className="w-full mt-6 bg-red-600 hover:bg-red-700 text-white py-6 text-lg"
                  >
                    <Link to="/contact">Get Started Today</Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Who Is It For Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-black mb-4">
              Who is the Solo option for?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              The Solo franchise is designed for qualified ADIs and PDIs who want the best of both worlds - independence with support.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {whoIsItFor.map((item) => (
              <Card key={item.title} className="border border-gray-200 hover:border-red-300 hover:shadow-lg transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Star className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-black mb-2">{item.title}</h3>
                      <p className="text-gray-600">{item.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-black mb-4">
              Why choose Top Driver?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Join Liverpool's trusted automatic driving school
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit) => (
              <Card key={benefit.title} className="text-center border-0 shadow-lg">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="text-xl font-bold text-black mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* All Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-black mb-4">
              What's Included
            </h2>
            <p className="text-lg text-gray-600">
              Everything you get with the Solo franchise for just £50/week
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {soloFeatures.map((feature) => (
              <div key={feature} className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0" />
                <span className="text-gray-700 font-medium">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-black mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Got questions? We've got answers.
            </p>
          </div>

          <div className="space-y-6">
            {faqs.map((faq) => (
              <Card key={faq.question} className="border border-gray-200">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-black mb-2">{faq.question}</h3>
                  <p className="text-gray-600">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-red-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to join Top Driver?
          </h2>
          <p className="text-xl text-red-100 mb-8">
            Start earning more while keeping your independence. Apply today!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="bg-white hover:bg-gray-100 text-red-600 px-8 py-6 text-lg font-semibold"
            >
              <Link to="/contact">Apply Now</Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-red-600 px-8 py-6 text-lg font-semibold"
            >
              <a href="tel:07960087298">
                <Phone className="mr-2 w-5 h-5" />
                07960 087 298
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Not Qualified Yet Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-2xl font-bold text-black mb-4">
            Not yet a qualified ADI?
          </h3>
          <p className="text-gray-600 mb-6">
            We also offer comprehensive instructor training to help you become a fully qualified Approved Driving Instructor.
          </p>
          <Button
            asChild
            variant="outline"
            className="border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
          >
            <Link to="/instructor-training">
              Learn About Instructor Training
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
        </div>
      </section>
    </>
  );
}
